import React from 'react';
import cn from 'classnames';
import t from 'utils/translate';
import { INPUT_MASK } from 'admin/constants/common';
import { Label, LabelProps } from 'admin/components/common/Form/Label';
import { Errors } from 'admin/components/common/Form/Errors';
import { CharsCount } from 'admin/components/common/Form/CharsCount';
import {
	ReduxFieldInputTypes,
	ReduxFieldMetaTypes,
	maskValue,
	limitValue,
	getErrorData,
	ShowErrorsOn,
} from 'admin/components/common/Form/utils';
import commonStyles from '../common.scss';
import css from './TextField.scss';

enum View {
	hero = 'hero',
}

enum LimitPosition {
	in = 'in',
	out = 'out',
}

type InputHTMLAttributes = Pick<
	React.InputHTMLAttributes<HTMLInputElement>,
	| 'onChange'
	| 'onBlur'
	| 'onKeyDown'
	| 'onKeyUp'
	| 'className'
	| 'id'
	| 'placeholder'
	| 'readOnly'
	| 'disabled'
	| 'autoComplete'
	| 'autoFocus'
	| 'name'
	| 'type'
	| 'min'
	| 'max'
	| 'step'
	| 'list'
	| 'aria-label'
>;

type CustomProps = {
	mask?: (typeof INPUT_MASK)[keyof typeof INPUT_MASK];
	suffix?: React.ReactNode;
	isLabelUppercase?: boolean;
	value?: string;
	error?: string;
	label?: Omit<LabelProps, 'component'> | React.ReactNode;
	theme?: 'dark' | 'light';
	description?: string;
	limit?: { max?: number; position?: LimitPosition; show?: boolean };
	isRequired?: boolean;
	autoSelect?: boolean;
	showErrorsOn?: ShowErrorsOn;
	view?: View;
};

type ReduxWrappedFieldProps = {
	input?: ReduxFieldInputTypes;
	meta?: ReduxFieldMetaTypes;
};

type Props = InputHTMLAttributes & CustomProps & ReduxWrappedFieldProps;

// Helper to check if label is a LabelProps object
const isLabelObject = (label: any): label is Omit<LabelProps, 'component'> =>
	typeof label === 'object' && label !== null && !React.isValidElement(label) && 'children' in label;

/**
 * Form input component that can be used both as independent pure field or
 * passed to component prop of redux-form 'Field'
 *
 * @example independent
 *      <TextField
 *        name="username"
 *        label="Username"
 *        placeholder="username..."
 *        onChange={onChangeHandler}
 *        value={username}
 *      />
 *
 * @example redux-form
 *     <Field
 *        component={TextField}
 *        name="username"
 * 		  label="Username"
 * 		  placeholder="username..."
 * 		  onChange={onChangeHandler}
 * 		  value={username}
 * 	    />
 */
export const TextField = ({
	className,
	input,
	label,
	meta,
	onChange: onPureInputChange,
	onBlur: onPureInputBlur,
	value = '',
	id = '',
	name = '',
	mask,
	description,
	placeholder,
	error = '',
	theme = 'light',
	isLabelUppercase = true,
	view,
	isRequired,
	readOnly,
	limit: limitProp,
	suffix,
	autoSelect = false,
	showErrorsOn = 'touched',
	type = 'text',
	...props
}: Props) => {
	const inputRef = React.useRef<HTMLInputElement>(null);
	const limit = { show: true, ...limitProp };
	const targetValue = input ? `${input.value === undefined ? '' : input.value}` : value;
	const inputId = meta && input ? `${meta.form}.${input.name}` : id || name;
	const inputValue = limit.max ? limitValue(targetValue, limit.max) : targetValue;
	const errorData = getErrorData({ meta, input, error, showErrorsOn });

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (props.disabled) return;

		if (input) {
			if (mask) {
				input.onChange(maskValue(e.target.value, mask));
			} else if (type === 'number') {
				const rawValue = e.target.value;
				const num = Number(e.target.value);
				input.onChange(rawValue !== '' && rawValue !== undefined && Number.isFinite(num) ? num : '');
			} else {
				input.onChange(e.target.value);
			}
		} else {
			onPureInputChange?.(e);
		}
	};

	React.useEffect(() => {
		if (props.autoFocus) inputRef.current?.focus();
	}, [props.autoFocus]);

	return (
		<div
			className={cn(css.inputField, css[theme], className, {
				[css.hasError]: errorData.show,
				[css.readOnly]: readOnly,
				[css.hero]: view === View.hero,
				[css.limitIn]: limit.position === LimitPosition.in, // position of <CharsCount />
				[commonStyles.disabled]: props.disabled,
			})}
		>
			<label className={css.inputLabel} htmlFor={inputId}>
				{label && (
					<Label
						theme={isLabelObject(label) ? label.theme ?? theme : theme}
						className={cn(
							css.labelText,
							{ [css.upper]: isLabelUppercase },
							isLabelObject(label) ? label.className : undefined
						)}
						weight={isLabelObject(label) ? label.weight : undefined}
						component="div"
					>
						{isLabelObject(label) ? label.children : label}
						{!isRequired && <span>{t('form.optional')}</span>}
					</Label>
				)}
				<div className={css.inputW}>
					<input
						name={name}
						{...input}
						{...props}
						readOnly={readOnly}
						type={type}
						onFocus={event => {
							input?.onFocus(event);
							if (autoSelect) event.target.select();
						}}
						onBlur={event => {
							input?.onBlur(event);
							onPureInputBlur?.(event);
						}}
						onChange={onChange}
						value={inputValue}
						id={inputId}
						placeholder={placeholder}
						ref={inputRef}
					/>
					{suffix && <div className={css.suffix}>{suffix}</div>}
					{limit.show && limit?.max !== undefined && inputValue !== undefined && (
						<CharsCount max={limit.max} value={inputValue} className={cn(css.fieldCharsCount)} />
					)}
				</div>
				<Errors className={css.error} show={errorData.show}>
					{errorData.message}
				</Errors>
				{description && <span className={css.description}>{description}</span>}
			</label>
		</div>
	);
};

TextField.view = View;

TextField.limitPosition = LimitPosition;

TextField.mask = {
	card: INPUT_MASK.CARD,
	expDate: INPUT_MASK.EXP_DATE,
	workspaceName: INPUT_MASK.WS_NAME,
	number: INPUT_MASK.NUMBER,
};
